/**
 * Per-item detail (route `/vaults/:vaultId/items/:itemId`). Loads the vault's
 * items, finds the one addressed by the URL, and renders the full `ItemCard`
 * (reveal / copy / inline edit / delete). Deep-linking works: a cold load of
 * this URL loads + decrypts the vault then shows the item. Delete returns to the
 * vault overview.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ItemCard, useClipboardCopy } from '../components/items';
import { useVaultItems } from '../store/vault-data';
import { ErrorNote } from '../components/ui';

export function ItemDetail() {
  const { vaultId, itemId } = useParams();
  const navigate = useNavigate();
  const { items, loading, error, notice, saveItem, deleteItem } = useVaultItems(vaultId);
  const { copiedId, copy } = useClipboardCopy();

  const backTo = `/vaults/${vaultId}`;
  const item = items.find((i) => i.id === itemId);

  return (
    <div className="space-y-4">
      <Link
        to={backTo}
        className="inline-block text-sm text-text-secondary underline-offset-2 hover:text-text hover:underline"
      >
        ‹ Back to vault
      </Link>

      {error !== null && <ErrorNote>{error}</ErrorNote>}
      {notice !== null && (
        <p
          role="status"
          className="rounded-md border border-border bg-surface-alt px-3 py-2 text-sm text-text-secondary"
        >
          {notice}
        </p>
      )}

      {loading && item === undefined && <p className="text-sm text-text-secondary">Loading…</p>}

      {!loading && item === undefined && (
        <p className="text-sm text-text-secondary">
          This item no longer exists.{' '}
          <Link to={backTo} className="text-accent-text underline">
            Back to vault
          </Link>
          .
        </p>
      )}

      {item !== undefined && (
        <ItemCard
          item={item}
          onSave={(id, version, payload) => saveItem(id, version, payload)}
          onDelete={async (id) => {
            await deleteItem(id);
            navigate(backTo);
          }}
          onCopy={copy}
          copied={copiedId === item.id}
        />
      )}
    </div>
  );
}
